import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CategoryPanel from './CategoryPanel';

const drawerWidth = 280;

const POSCategorySideNav = ({
  mobileOpen,
  onMobileClose,
  onSwitchToMainView,
  categories,
  selectedCategory,
  onSelectCategory,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [notificationVisible, setNotificationVisible] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handleNotificationVisibility = (e) => setNotificationVisible(e.detail.isVisible);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('offlineNotificationVisibility', handleNotificationVisibility);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('offlineNotificationVisibility', handleNotificationVisibility);
    };
  }, []);

  const handleCategorySelect = (category) => {
    onSelectCategory(category);
    // Close mobile drawer when category is selected on mobile
    if (isMobile && mobileOpen) {
      onMobileClose();
    }
  };

  const drawer = (
    <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#667eea' }}>
          {t('point_of_sale') || 'Point of Sale'}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={onSwitchToMainView}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#667eea',
            color: '#667eea',
            '&:hover': {
              borderColor: '#5568d3',
              backgroundColor: 'rgba(102, 126, 234, 0.04)',
            },
          }}
        >
          {t('switch_to_main_view') || 'Switch to Main View'}
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <CategoryPanel
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={handleCategorySelect}
        />
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: notificationVisible ? { xs: 64 + 28, sm: 64 + 22 } : 64,
            height: notificationVisible ? { xs: 'calc(100vh - 92px)', sm: 'calc(100vh - 86px)' } : 'calc(100vh - 64px)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            position: 'fixed',
            transition: 'top 0.3s ease-in-out, height 0.3s ease-in-out',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default POSCategorySideNav;
